import React, { useState, useEffect, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import html2canvas from 'html2canvas';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { tutorService } from '../services/tutorService';
import { toast } from 'sonner';
import {
  Download,
  Share2,
  RefreshCw,
  BadgeCheck,
  BadgeIcon,
  Clock,
  QrCode,
  User,
  BookOpen,
  Award,
  AlertCircle,
  Loader2,
} from 'lucide-react';

const TutorIdCard = () => {
  const [idCard, setIdCard] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    loadIdCardData();
  }, []);

  const loadIdCardData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [idCardData, statsData] = await Promise.all([
        tutorService.getIdCard(),
        tutorService.getIdCardStats(),
      ]);
      setIdCard(idCardData);
      setStats(statsData);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load ID card');
      toast.error('Failed to load ID card');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!cardRef.current) return;

    setIsDownloading(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        scale: 3,
        backgroundColor: null,
        useCORS: true,
        allowTaint: true,
      });

      const link = document.createElement('a');
      link.download = `priedu_id_card_${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();

      toast.success('ID card downloaded successfully!');
    } catch (err) {
      toast.error('Failed to download ID card');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleShare = async () => {
    if (!idCard?.profile_url) {
      toast.error('No profile URL available to share');
      return;
    }

    setIsSharing(true);
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'My PriEdu Tutor ID Card',
          text: 'View my verified tutor profile on PriEdu',
          url: idCard.profile_url,
        });
        toast.success('Shared successfully!');
      } else {
        await navigator.clipboard.writeText(idCard.profile_url);
        toast.success('Profile link copied to clipboard!');
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        toast.error('Failed to share');
      }
    } finally {
      setIsSharing(false);
    }
  };

  const handleRegenerateQrCode = async () => {
    const confirmed = window.confirm(
      'This will generate a new QR code. Your old QR code will no longer work. Continue?'
    );
    if (!confirmed) return;

    setIsRegenerating(true);
    try {
      await tutorService.regenerateQrCode();
      await loadIdCardData();
      toast.success('QR code regenerated successfully!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to regenerate QR code');
    } finally {
      setIsRegenerating(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  const getExperienceLabel = (years) => {
    if (!years) return 'New Tutor';
    if (years === 1) return '1 Year Experience';
    return `${years} Years Experience`;
  };

  const getTutorTypeLabel = (type) => {
    return type === 'academic' ? 'Academic Tutor' : 'Activity Tutor';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading ID Card...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center max-w-md mx-auto p-6">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Failed to Load ID Card</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button onClick={loadIdCardData} className="bg-blue-600 hover:bg-blue-700">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (!idCard) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center max-w-md mx-auto p-6">
          <BadgeIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">No ID Card Available</h2>
          <p className="text-gray-600">Complete your profile to generate your digital ID card</p>
        </div>
      </div>
    );
  }

  const tutorInfo = idCard.tutor_info || {};

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">My Digital ID Card</h1>
        <p className="text-gray-600">Your verified tutor identification</p>
      </div>

      <div ref={cardRef}>
        <div className="bg-gradient-to-br from-blue-600 to-teal-500 rounded-2xl shadow-xl overflow-hidden">
          <div className="text-center py-5">
            <h2 className="text-2xl font-bold text-white tracking-widest">PriEdu</h2>
            <p className="text-white/90 text-sm tracking-wider font-semibold mt-1">
              VERIFIED TUTOR ID CARD
            </p>
          </div>

          <div className="bg-white rounded-[18px] m-1.5 p-6">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-600 to-teal-500 flex items-center justify-center flex-shrink-0 border-4 border-white shadow-lg">
                {tutorInfo.photo ? (
                  <img
                    src={tutorInfo.photo}
                    alt={tutorInfo.name}
                    className="w-full h-full rounded-full object-cover"
                    crossOrigin="anonymous"
                  />
                ) : (
                  <User className="h-10 w-10 text-white" />
                )}
              </div>

              <div className="flex-1 min-w-0">
                <h3 className="text-xl font-bold text-gray-900 truncate">{tutorInfo.name}</h3>
                <p className="text-blue-600 font-semibold">{getTutorTypeLabel(tutorInfo.tutor_type)}</p>
                <p className="text-gray-500 text-sm">{getExperienceLabel(tutorInfo.experience_years)}</p>
              </div>
            </div>

            {tutorInfo.subjects && tutorInfo.subjects.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center gap-2 text-gray-600 mb-2">
                  <BookOpen className="h-4 w-4" />
                  <span className="text-sm font-medium">Subjects</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {tutorInfo.subjects.slice(0, 4).map((subject, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-blue-50 text-blue-700 text-sm rounded-full font-medium"
                    >
                      {subject}
                    </span>
                  ))}
                  {tutorInfo.subjects.length > 4 && (
                    <span className="px-3 py-1 bg-gray-100 text-gray-600 text-sm rounded-full">
                      +{tutorInfo.subjects.length - 4} more
                    </span>
                  )}
                </div>
              </div>
            )}

            <div className="flex justify-center mb-4">
              <div className="p-4 bg-white rounded-xl border-2 border-gray-200">
                <QRCodeSVG
                  value={idCard.profile_url || idCard.tutor_id_code}
                  size={180}
                  level="H"
                  bgColor="#ffffff"
                  fgColor="#000000"
                />
                <p className="text-gray-500 text-xs text-center mt-3 font-medium">
                  Scan to verify tutor identity
                </p>
              </div>
            </div>

            <div className="flex justify-center mb-4">
              <div className="px-4 py-3 bg-gray-50 rounded-lg border border-gray-200 flex items-center gap-2">
                <BadgeIcon className="h-4 w-4 text-gray-500" />
                <span className="font-mono font-semibold text-gray-800">
                  ID: {idCard.tutor_id_code}
                </span>
              </div>
            </div>

            <div className="flex justify-center">
              <div className="px-4 py-2.5 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-500 flex items-center gap-2">
                <BadgeCheck className="h-5 w-5 text-green-600" />
                <span className="text-green-700 font-bold text-sm tracking-wide">
                  VERIFIED TUTOR
                </span>
              </div>
            </div>
          </div>

          <div className="text-center py-4">
            <p className="text-white/80 text-sm">Valid until profile is active</p>
            <p className="text-white font-semibold">priedu.com</p>
          </div>
        </div>
      </div>

      <div className="flex gap-3 mt-6">
        <Button
          onClick={handleDownload}
          disabled={isDownloading}
          className="flex-1 bg-gradient-to-r from-blue-600 to-teal-500 hover:from-blue-700 hover:to-teal-600 text-white py-6"
        >
          {isDownloading ? (
            <Loader2 className="h-5 w-5 mr-2 animate-spin" />
          ) : (
            <Download className="h-5 w-5 mr-2" />
          )}
          {isDownloading ? 'Downloading...' : 'Download'}
        </Button>
        <Button
          onClick={handleShare}
          disabled={isSharing}
          variant="outline"
          className="flex-1 border-blue-600 text-blue-600 hover:bg-blue-50 py-6"
        >
          {isSharing ? (
            <Loader2 className="h-5 w-5 mr-2 animate-spin" />
          ) : (
            <Share2 className="h-5 w-5 mr-2" />
          )}
          {isSharing ? 'Sharing...' : 'Share'}
        </Button>
        <Button
          onClick={handleRegenerateQrCode}
          disabled={isRegenerating}
          variant="outline"
          className="border-gray-300 text-gray-700 hover:bg-gray-50 py-6"
          title="Regenerate QR Code"
        >
          {isRegenerating ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <RefreshCw className="h-5 w-5" />
          )}
        </Button>
      </div>

      {stats && (
        <Card className="p-6 mt-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <QrCode className="h-5 w-5 text-blue-600" />
            QR Code Statistics
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
              <div className="flex items-center justify-center mb-2">
                <QrCode className="h-6 w-6 text-blue-600" />
              </div>
              <p className="text-2xl font-bold text-blue-600 text-center">{stats.total_scans || 0}</p>
              <p className="text-blue-600 text-sm text-center">Total Scans</p>
            </div>
            <div className="p-4 bg-teal-50 rounded-xl border border-teal-200">
              <div className="flex items-center justify-center mb-2">
                <Clock className="h-6 w-6 text-teal-600" />
              </div>
              <p className="text-lg font-bold text-teal-600 text-center">
                {formatDate(stats.last_scanned_at)}
              </p>
              <p className="text-teal-600 text-sm text-center">Last Scanned</p>
            </div>
          </div>
        </Card>
      )}

      <Card className="p-6 mt-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Award className="h-5 w-5 text-blue-600" />
          How to Use
        </h3>
        <div className="space-y-3">
          {[
            'Show this ID card when visiting students',
            'Let parents scan the QR code to verify',
            'They can view your profile and credentials',
            'Download or share your ID card anytime',
          ].map((instruction, index) => (
            <div key={index} className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                <span className="text-white text-xs font-bold">{index + 1}</span>
              </div>
              <p className="text-gray-700">{instruction}</p>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default TutorIdCard;
